import React, { useEffect, useRef, useState } from "react";
import InputError from "./InputError";
import Select, { components } from "react-select";
import getClassName from "../../utilities/getClassName";
import Label from "./Label";
import Fieldset from "./Fieldset";
import defaultChevron from "../../assets/icons/chevron-down-dark-grey.svg";

/** Option to display for a stored value, or a synthetic option if unmatched. */
function optionFromValue(options, val) {
    if (val == null || val === "") return undefined;
    const needle = String(val).trim().toLowerCase();
    return (
        (options || []).find((option) => String(option.value).trim().toLowerCase() === needle) || {
            value: val,
            label: String(val),
        }
    );
}

function SelectInput({
    value,
    defaultValue,
    placeholder,
    label,
    options,
    search,
    disabled,
    required,
    validateForm,
    classNames,
    chevron,
    icon,
    menuPlacement,
    onFocus,
    copiedProperty,
    allowChange,
    skeleton,
    ariaLabel,
}) {
    // State
    const [selectedOption, setSelectedOption] = useState(() => optionFromValue(options, defaultValue));
    const [error, setError] = useState("");
    const [text, setText] = useState("");
    const [, setTypedOption] = useState(null);
    // const [focus, setFocus] = useState(null)

    // Add class styles
    let className = ["select-input"];
    if (typeof classNames === "object") {
        className = className.concat(classNames);
    }
    if (error) {
        className.push("select-input--error");
    }
    if (disabled) {
        className.push("select-input--disabled");
    }

    const validateHandler = (selectedOption) => {
        switch (true) {
            case required && !selectedOption:
                setError(`${label.replace("*", "")} is required`);
                return;
            case required && selectedOption.length === 0:
                setError(`${label.replace("*", "")} is required`);
                return;
            default:
                setError("");
                return;
        }
    };

    useEffect(() => {
        if (copiedProperty) {
            const option = copiedProperty ? copiedProperty : selectedOption;
            setSelectedOption(option);
            value(option.value);
            validateHandler(option);
            setTypedOption(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update selected option
    const selectChangeHandler = (selected) => {
        // const option = typedOption ? typedOption : selected
        const option = selected;
        setSelectedOption(option);
        value(option.value);
        validateHandler(option);
        setTypedOption(null);
    };

    // Force validation
    useEffect(() => {
        if (validateForm) {
            validateHandler(selectedOption);
        }
    });

    // Set default value
    useEffect(() => {
        if (!defaultValue) return;
        if (!selectedOption || allowChange) {
            setSelectedOption(optionFromValue(options, defaultValue));
        }
    }, [defaultValue, options, selectedOption, allowChange]);

    // Custom dropdown chevron
    const DropdownIndicator = (props) => {
        return (
            components.DropdownIndicator && (
                <components.DropdownIndicator {...props}>
                    <img src={chevron ? chevron : defaultChevron} alt="" />
                </components.DropdownIndicator>
            )
        );
    };

    // Focus on option select
    const Option = (props) => {
        const ref = useRef();

        useEffect(() => {
            props.isSelected && text && ref.current.scrollIntoView({ block: "nearest" });
        }, [props]);

        // if (focus && props.data.value === focus.value) {
        //     console.log("here...")
        // }

        return (
            components.Option && (
                <components.Option {...props}>
                    <div ref={ref}>{props.children}</div>
                </components.Option>
            )
        );
    };

    const isLetter = (value) => {
        return /[a-zA-Z]/.test(value) && value.length === 1;
    };

    // Held in a ref so the timeout survives re-renders and can be cancelled.
    const clearTextRef = useRef(null);

    useEffect(() => {
        if (text !== "") {
            const result = options.find((element) => {
                return element.label.toLowerCase().indexOf(text.toLowerCase()) === 0;
            });

            if (result) {
                // setFocus(result)
                setTypedOption(result);
                setSelectedOption(result);
            }

            clearTextRef.current = setTimeout(() => {
                setText("");
                // setFocus(null)
            }, 1000);
        }

        return () => {
            if (clearTextRef.current) {
                clearTimeout(clearTextRef.current);
                clearTextRef.current = null;
            }
        };
    }, [text, options]);

    const focus = () => {
        if (onFocus) {
            onFocus();
        }
    };

    return (
        <Fieldset skeleton={skeleton}>
            <div style={icon ? { backgroundImage: `url(${icon})` } : {}} className={getClassName(className)}>
                <Select
                    aria-label={ariaLabel || label}
                    value={selectedOption}
                    components={{ DropdownIndicator, Option }}
                    isDisabled={disabled}
                    isSearchable={!!search}
                    classNamePrefix="select-input"
                    onFocus={focus}
                    onChange={selectChangeHandler}
                    onKeyDown={(e) => {
                        if (clearTextRef.current) {
                            clearTimeout(clearTextRef.current);
                            clearTextRef.current = null;
                        }
                        if (isLetter(e.key)) {
                            setText(`${text}${e.key}`);
                        }
                    }}
                    options={options}
                    menuPlacement={menuPlacement}
                    placeholder={placeholder}
                />
                {error && <InputError>{error}</InputError>}
                {label && <Label minimised={true}>{label}</Label>}
            </div>
        </Fieldset>
    );
}

export default SelectInput;
